import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { MaintenancePage } from "@/pages/public/MaintenancePage";
import { Spinner } from "@/components/ui/spinner";

interface MaintenanceStatus {
  apiMaintenance: boolean;
  fullMaintenance: boolean;
  active: boolean;
  ipWhitelisted: boolean;
  message: string | null;
  estimatedEnd: string | null;
  clientIp: string;
}

/**
 * MaintenanceGuard - Kontrollerar underhållsstatus och visar underhållssida vid behov
 *
 * Logik:
 * 1. Hämtar /maintenance-status från API:t
 * 2. Om NÅGON maintenance är aktivt (apiMaintenance ELLER fullMaintenance):
 *    - IP-adresser i whitelist släpps igenom
 *    - Super admins släpps igenom (kan stänga av underhållsläge)
 *    - /admin-routes tillåts (för inloggning till admin)
 *    - Alla andra ser underhållssidan
 */
export function MaintenanceGuard({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<MaintenanceStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
        const response = await fetch(`${apiUrl}/maintenance-status`);

        if (!response.ok) {
          // Om vi inte kan nå API:t, visa inte maintenance page (kan vara nätverksfel)
          setError(true);
          setIsLoading(false);
          return;
        }

        const data = await response.json();
        if (data.success && data.data) {
          setStatus(data.data);
        }
      } catch {
        // Nätverksfel - låt användaren fortsätta (bättre UX än att blocka)
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkMaintenance();

    // Kontrollera status var 30:e sekund
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);

  // Laddar status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Spinner size="lg" />
      </div>
    );
  }

  // Om vi inte kunde hämta status - BLOCKERA för säkerhets skull
  // (bättre att visa fel än att låta användare kringgå underhåll)
  if (error) {
    return (
      <MaintenancePage
        message="Kunde inte kontrollera systemstatus. Försök igen senare."
        estimatedEnd={null}
      />
    );
  }

  // Om status inte laddats ännu (borde inte hända pga isLoading-check ovan)
  if (!status) {
    return <>{children}</>;
  }

  // Kolla om vi ska visa underhållssida (båda lägena blockerar nu)
  if (status.active) {
    // IP-adresser i whitelist passerar alltid
    if (status.ipWhitelisted) {
      return <>{children}</>;
    }

    // Super admin får alltid passera
    if (user?.role === "super_admin") {
      return <>{children}</>;
    }

    // Admin-routes tillåts (för inloggning)
    if (location.pathname.startsWith("/admin")) {
      return <>{children}</>;
    }

    // Visa underhållssida för alla andra
    return (
      <MaintenancePage
        message={status.message}
        estimatedEnd={status.estimatedEnd}
      />
    );
  }

  // Ingen maintenance aktiv - visa normal sida
  return <>{children}</>;
}
